import { normalizeApiError } from './error-utils';
import { client } from './generated/client.gen';
import { getBearerToken } from '@/src/auth/token-storage';

export const apiAuthAccessErrorEvent = 'bitrix-node:api-auth-access-error';
export const apiActivityBackgroundRequestHeader =
  'x-sparkone-background-request';

const pendingActivityRequests = new WeakSet<Request>();
const retryTemplates = new WeakMap<Request, Request>();
const managedBearerRequests = new WeakSet<Request>();
const apiActivityListeners = new Set<() => void>();
const apiActivityDelayMs = 150;
let pendingApiActivityCount = 0;
let visibleApiActivityCount = 0;
let apiActivityDelayTimer: ReturnType<typeof setTimeout> | null = null;

function emitApiActivityChange() {
  for (const listener of apiActivityListeners) {
    listener();
  }
}

function clearApiActivityDelayTimer() {
  if (!apiActivityDelayTimer) {
    return;
  }

  clearTimeout(apiActivityDelayTimer);
  apiActivityDelayTimer = null;
}

function setVisibleApiActivityCount(nextCount: number) {
  if (visibleApiActivityCount === nextCount) {
    return;
  }

  visibleApiActivityCount = nextCount;
  emitApiActivityChange();
}

function scheduleApiActivityDisplay() {
  if (apiActivityDelayTimer || visibleApiActivityCount > 0) {
    return;
  }

  apiActivityDelayTimer = setTimeout(() => {
    apiActivityDelayTimer = null;
    if (pendingApiActivityCount > 0) {
      setVisibleApiActivityCount(pendingApiActivityCount);
    }
  }, apiActivityDelayMs);
}

function bearerAuthorization(token: string) {
  return `Bearer ${token}`;
}

function isBackgroundApiRequest(request: Request) {
  return request.headers.get(apiActivityBackgroundRequestHeader) === 'true';
}

function beginApiActivity(request: Request) {
  try {
    retryTemplates.set(request, request.clone());
  } catch {
    // A consumed or otherwise non-cloneable request cannot be safely replayed.
  }

  const bearerToken = getBearerToken();
  if (
    bearerToken &&
    request.headers.get('authorization') === bearerAuthorization(bearerToken)
  ) {
    managedBearerRequests.add(request);
  }

  if (isBackgroundApiRequest(request)) {
    return;
  }

  pendingActivityRequests.add(request);
  pendingApiActivityCount += 1;

  if (visibleApiActivityCount > 0) {
    setVisibleApiActivityCount(pendingApiActivityCount);
    return;
  }

  scheduleApiActivityDisplay();
}

function endApiActivity(request?: Request) {
  if (!request) {
    return;
  }

  const trackedActivity = pendingActivityRequests.delete(request);
  retryTemplates.delete(request);
  managedBearerRequests.delete(request);
  if (!trackedActivity) {
    return;
  }

  pendingApiActivityCount = Math.max(0, pendingApiActivityCount - 1);

  if (pendingApiActivityCount === 0) {
    clearApiActivityDelayTimer();
    setVisibleApiActivityCount(0);
    return;
  }

  if (visibleApiActivityCount > 0) {
    setVisibleApiActivityCount(pendingApiActivityCount);
  }
}

export function getApiActivitySnapshot() {
  return visibleApiActivityCount;
}

export function subscribeToApiActivity(listener: () => void) {
  apiActivityListeners.add(listener);

  return () => {
    apiActivityListeners.delete(listener);
  };
}

function emitAuthAccessError(error: unknown, response?: Response, options?: { url?: string }) {
  const details = normalizeApiError(error);
  const statusCode = details.statusCode ?? response?.status ?? null;

  // A 403 is an authenticated authorization or domain decision. Refreshing
  // credentials cannot change it and can retrigger the rejected operation.
  if (statusCode !== 401) {
    return;
  }

  if (options?.url === '/auth/refresh') {
    return;
  }

  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent(apiAuthAccessErrorEvent, { detail: { ...details, statusCode } }));
}

async function recoverUnauthorizedResponse(
  response: Response,
  request: Request,
  options: { fetch?: typeof globalThis.fetch },
): Promise<Response> {
  if (response.status !== 401 || !managedBearerRequests.has(request)) {
    return response;
  }

  const retryTemplate = retryTemplates.get(request);
  if (!retryTemplate) {
    return response;
  }

  const attemptedAuthorization = request.headers.get('authorization');
  let bearerToken = getBearerToken();

  // Another request may already have completed the shared refresh while this
  // 401 was travelling back. In that case, reuse the newly installed token
  // instead of starting a second refresh generation.
  if (!bearerToken || bearerAuthorization(bearerToken) === attemptedAuthorization) {
    const { refreshStoredTenantSession } = await import('@/src/auth/session-refresh');
    const result = await refreshStoredTenantSession();
    if (result.status !== 'refreshed') {
      return response;
    }
    bearerToken = getBearerToken();
  }

  if (!bearerToken) {
    return response;
  }

  const headers = new Headers(retryTemplate.headers);
  headers.set('authorization', bearerAuthorization(bearerToken));
  const retryRequest = new Request(retryTemplate, { headers });
  const fetchRequest = options.fetch ?? globalThis.fetch;

  // This direct replay deliberately bypasses the interceptor chain. The
  // original request pipeline will parse the returned response, and a second
  // 401 will flow to the normal auth-expired event without another retry.
  return fetchRequest(retryRequest);
}

client.setConfig({
  baseUrl: process.env.NEXT_PUBLIC_API_BASE_URL ?? 'http://localhost:3001',
  credentials: 'include',
});

client.interceptors.request.use((request) => {
  beginApiActivity(request);
  return request;
});

client.interceptors.response.use(async (response, request, options) => {
  try {
    return await recoverUnauthorizedResponse(response, request, options);
  } finally {
    endApiActivity(request);
  }
});

client.interceptors.error.use((error, response, _request, options) => {
  endApiActivity(_request);
  emitAuthAccessError(error, response, options);
  return error;
});

export { client };
